<template>
  <div class="page-jump flex items-end gap-2 md:ml-3">
    <label class="whitespace-nowrap mb-0" :for="inputId">Go to page</label>
    <input
      :id="inputId"
      v-model="jumpTarget"
      type="number"
      min="1"
      :max="totalPages"
      :aria-label="`Go to page, 1 to ${totalPages}`"
      class="page-jump-input form-control"
      data-cy="pagination-jump-input"
      @keyup.enter="submit"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { generateUID } from "@/lib/helpers";

const props = defineProps<{
  totalPages: number;
  currentPage: number;
}>();

const emit = defineEmits<{ jump: [page: number] }>();

// Unique per instance: two paginated lists can share a page, and a duplicated
// id would point every label at the first input.
const inputId = `pagination-jump-input-${generateUID()}`;

const jumpTarget = ref("");

const submit = () => {
  const parsed = Number.parseInt(String(jumpTarget.value).trim(), 10);
  jumpTarget.value = "";
  if (Number.isNaN(parsed)) return;
  // Clamp rather than reject. Page 0, -3 or 900-of-250 is a typo, and passing
  // it on would fetch a page that does not exist and blank the table.
  const page = Math.min(Math.max(parsed, 1), props.totalPages);
  if (page === props.currentPage) return;
  emit("jump", page);
};
</script>

<style>
.page-jump {
  /* A redraw can leave two of these teleported into the same portal. Only the
     last one shows. Deliberately :has() and not :last-of-type - see the same
     note in DatatablePageLength for what that pseudo-class does when an
     unrelated sibling of the same tag turns up. */
  display: flex;
  &:has(~ .page-jump) {
    display: none;
  }
}
.page-jump-input {
  width: 72px;
}
</style>
